import { create } from 'zustand';
import type { ChatConversation, ChatHistory, ChatMessageView, ChatReadEvent } from '@tg/shared';
import { isApiRequestError, newIdempotencyKey, type ClientErrorCode } from '@/api/client';
import { useSession } from '@/stores/session';
import { chatApi } from './api';

/**
 * Chat state shared by the friends page (list + open dialog) and the global listener (socket events, toasts).
 * - `conversations` — GET /api/chat (null until loaded);
 * - `threads[publicId]` — the loaded history (oldest first) plus the optimistic messages still on the way;
 * - `open` — the dialog on screen (the message toast is not shown for it, it gets read marks).
 * Every outgoing message carries a `clientId`: a resend (the retry button, the network coming back) repeats the
 * same id, so the server stores it once; the socket copy with that id replaces the optimistic bubble.
 */

export interface PendingMessage {
  clientId: string;
  text: string;
  createdAt: string;
  state: 'sending' | 'failed';
  /** why the last try failed (NETWORK → retried automatically when the connection is back) */
  code?: ClientErrorCode;
}

export type ChatPartner = ChatHistory['partner'];

export interface Thread {
  partner: ChatPartner | null;
  messages: ChatMessageView[];
  pending: PendingMessage[];
  hasMore: boolean;
  status: 'loading' | 'ready' | 'error';
  loadingOlder: boolean;
}

export interface BlockedPlayer {
  publicId: number;
  nickname: string;
  avatar: string;
}

export interface SendError {
  code: ClientErrorCode;
  params: Record<string, string | number>;
}

interface ChatState {
  conversations: ChatConversation[] | null;
  listError: boolean;
  threads: Record<number, Thread>;
  open: number | null;
  /** players I blocked — GET /api/friends/blocked (null until loaded) */
  blocked: BlockedPlayer[] | null;
}

const EMPTY: ChatState = { conversations: null, listError: false, threads: {}, open: null, blocked: null };

export const useChat = create<ChatState>()(() => ({ ...EMPTY }));

const myId = (): number | null => useSession.getState().me?.publicId ?? null;
const partnerOf = (m: ChatMessageView, me: number | null): number => (m.from === me ? m.to : m.from);
const errOf = (e: unknown): SendError =>
  isApiRequestError(e) ? { code: e.code, params: e.params } : { code: 'INTERNAL', params: {} };

/** unique by id, oldest first (ISO timestamps compare as strings; ties keep their order) */
export function mergeMessages(a: ChatMessageView[], b: ChatMessageView[]): ChatMessageView[] {
  const seen = new Set<string>();
  const out: ChatMessageView[] = [];
  for (const m of [...a, ...b]) {
    if (seen.has(m.id)) continue;
    seen.add(m.id);
    out.push(m);
  }
  return out
    .map((m, i) => ({ m, i }))
    .sort((x, y) => (x.m.createdAt < y.m.createdAt ? -1 : x.m.createdAt > y.m.createdAt ? 1 : x.i - y.i))
    .map((x) => x.m);
}

const byNewest = (a: ChatConversation, b: ChatConversation): number =>
  (b.lastMessage?.createdAt ?? '').localeCompare(a.lastMessage?.createdAt ?? '');

function patchThread(pid: number, fn: (t: Thread) => Partial<Thread>): void {
  useChat.setState((s) => {
    const t = s.threads[pid];
    if (!t) return s;
    return { threads: { ...s.threads, [pid]: { ...t, ...fn(t) } } };
  });
}

function patchConversation(pid: number, fn: (c: ChatConversation) => Partial<ChatConversation>): void {
  useChat.setState((s) =>
    s.conversations
      ? { conversations: s.conversations.map((c) => (c.publicId === pid ? { ...c, ...fn(c) } : c)) }
      : s,
  );
}

let listSeq = 0;

export async function loadConversations(): Promise<void> {
  const seq = ++listSeq;
  try {
    const list = await chatApi.list();
    if (seq !== listSeq) return;
    useChat.setState({ conversations: [...list].sort(byNewest), listError: false });
  } catch {
    if (seq === listSeq) useChat.setState((s) => ({ listError: s.conversations === null }));
  }
}

/** first page of the history (again after an error); keeps optimistic messages and socket arrivals */
export async function loadThread(pid: number): Promise<void> {
  useChat.setState((s) => {
    const t = s.threads[pid];
    const base: Thread = t ?? {
      partner: null,
      messages: [],
      pending: [],
      hasMore: false,
      status: 'loading',
      loadingOlder: false,
    };
    return {
      threads: { ...s.threads, [pid]: { ...base, status: t?.status === 'ready' ? 'ready' : 'loading' } },
    };
  });
  try {
    const h = await chatApi.history(pid);
    patchThread(pid, (t) => ({
      partner: h.partner,
      messages: mergeMessages(h.messages, t.messages),
      // a reload of the newest page keeps the older pages already shown
      hasMore: t.status === 'ready' ? t.hasMore : h.hasMore,
      status: 'ready',
    }));
    patchConversation(pid, () => ({ canWrite: h.partner.canWrite }));
  } catch {
    patchThread(pid, (t) => (t.status === 'ready' ? {} : { status: 'error' }));
  }
}

export async function loadOlder(pid: number): Promise<void> {
  const t = useChat.getState().threads[pid];
  if (!t || t.status !== 'ready' || !t.hasMore || t.loadingOlder) return;
  const first = t.messages[0];
  if (!first) return;
  patchThread(pid, () => ({ loadingOlder: true }));
  try {
    const h = await chatApi.history(pid, first.id);
    patchThread(pid, (x) => ({
      messages: mergeMessages(h.messages, x.messages),
      hasMore: h.hasMore,
      loadingOlder: false,
    }));
  } catch {
    patchThread(pid, () => ({ loadingOlder: false }));
  }
}

let listReload: ReturnType<typeof setTimeout> | null = null;
function reloadListSoon(): void {
  if (listReload) return;
  listReload = setTimeout(() => {
    listReload = null;
    void loadConversations();
  }, 300);
}

/** a stored message (socket `chat:message` or the send answer) */
export function receive(m: ChatMessageView): void {
  const me = myId();
  const pid = partnerOf(m, me);
  // a message hidden by a moderator comes again with the same id: it replaces the stored one in place
  const update = m.hidden;
  patchThread(pid, (t) => ({
    messages: mergeMessages(
      update ? t.messages.map((x) => (x.id === m.id ? m : x)) : t.messages,
      [m],
    ),
    pending: m.clientId ? t.pending.filter((p) => p.clientId !== m.clientId) : t.pending,
  }));
  const s = useChat.getState();
  if (!s.conversations) return;
  const c = s.conversations.find((x) => x.publicId === pid);
  if (!c) return reloadListSoon();
  if (update) {
    // the unread counter skips hidden messages on the server: take the list from there
    if (c.lastMessage?.id === m.id) patchConversation(pid, () => ({ lastMessage: m }));
    return reloadListSoon();
  }
  if (c.lastMessage?.id === m.id) return;
  const incoming = m.from !== me && m.readAt === null;
  const newer = !c.lastMessage || m.createdAt >= c.lastMessage.createdAt;
  const next: ChatConversation = {
    ...c,
    lastMessage: newer ? m : c.lastMessage,
    unread: c.unread + (incoming ? 1 : 0),
  };
  useChat.setState({ conversations: [next, ...s.conversations.filter((x) => x !== c)].sort(byNewest) });
}

/** socket `chat:read`: the partner has read my messages up to `upTo` */
export function applyRead(e: ChatReadEvent): void {
  const me = myId();
  const mark = (list: ChatMessageView[]): ChatMessageView[] => {
    const idx = list.findIndex((m) => m.id === e.upTo);
    const upToAt = idx >= 0 ? list[idx]!.createdAt : e.at;
    return list.map((m) =>
      m.from === me && m.to === e.by && m.readAt === null && m.createdAt <= upToAt
        ? { ...m, readAt: e.at }
        : m,
    );
  };
  patchThread(e.by, (t) => ({ messages: mark(t.messages) }));
  patchConversation(e.by, (c) => (c.lastMessage ? { lastMessage: mark([c.lastMessage])[0]! } : {}));
}

const readInFlight = new Map<number, string>();

/** the open dialog is on screen: every incoming message becomes read (POST …/read, then locally) */
export async function markRead(pid: number): Promise<void> {
  const me = myId();
  const t = useChat.getState().threads[pid];
  if (!t || t.status !== 'ready') return;
  const unread = t.messages.filter((m) => m.from === pid && m.from !== me && m.readAt === null);
  const last = unread[unread.length - 1];
  if (!last || readInFlight.get(pid) === last.id) return;
  readInFlight.set(pid, last.id);
  try {
    await chatApi.read(pid, last.id);
    const at = new Date().toISOString();
    const upToAt = last.createdAt;
    patchThread(pid, (x) => ({
      messages: x.messages.map((m) =>
        m.from === pid && m.readAt === null && m.createdAt <= upToAt ? { ...m, readAt: at } : m,
      ),
    }));
    patchConversation(pid, (c) => ({
      unread: 0,
      lastMessage:
        c.lastMessage && c.lastMessage.from === pid && c.lastMessage.readAt === null
          ? { ...c.lastMessage, readAt: at }
          : c.lastMessage,
    }));
  } catch {
    // the next change (a new message, the tab shown again) tries again
  } finally {
    if (readInFlight.get(pid) === last.id) readInFlight.delete(pid);
  }
}

async function deliver(pid: number, clientId: string): Promise<SendError | null> {
  const p = useChat.getState().threads[pid]?.pending.find((x) => x.clientId === clientId);
  if (!p) return null;
  patchThread(pid, (t) => ({
    pending: t.pending.map((x) =>
      x.clientId === clientId ? { ...x, state: 'sending', code: undefined } : x,
    ),
  }));
  try {
    const m = await chatApi.send(pid, p.text, clientId);
    receive(m);
    return null;
  } catch (e) {
    const err = errOf(e);
    // a network failure or a burst limit: the bubble stays with «Retry»; anything else is final — the text goes back
    const keep = err.code === 'NETWORK' || err.code === 'RATE_LIMITED';
    patchThread(pid, (t) => ({
      pending: keep
        ? t.pending.map((x) => (x.clientId === clientId ? { ...x, state: 'failed', code: err.code } : x))
        : t.pending.filter((x) => x.clientId !== clientId),
    }));
    if (
      err.code === 'CHAT_REJECTED' &&
      (err.params.reason === 'not_friends' || err.params.reason === 'blocked')
    ) {
      patchThread(pid, (t) => (t.partner ? { partner: { ...t.partner, canWrite: false } } : {}));
      patchConversation(pid, () => ({ canWrite: false }));
    }
    return err;
  }
}

/** optimistic send: the bubble shows at once; resolves with the error (null — stored) */
export function send(pid: number, text: string): Promise<SendError | null> {
  const clientId = newIdempotencyKey();
  const p: PendingMessage = { clientId, text, createdAt: new Date().toISOString(), state: 'sending' };
  patchThread(pid, (t) => ({ pending: [...t.pending, p] }));
  return deliver(pid, clientId);
}

export function retry(pid: number, clientId: string): Promise<SendError | null> {
  return deliver(pid, clientId);
}

export function discard(pid: number, clientId: string): void {
  patchThread(pid, (t) => ({ pending: t.pending.filter((x) => x.clientId !== clientId) }));
}

/** the connection is back: resend what failed on the network (same clientId — never twice) */
export function retryNetworkFailed(): void {
  for (const [pid, t] of Object.entries(useChat.getState().threads)) {
    for (const p of t.pending)
      if (p.state === 'failed' && p.code === 'NETWORK') void deliver(Number(pid), p.clientId);
  }
}

export function setOpen(pid: number | null): void {
  useChat.setState({ open: pid });
}

let blockedSeq = 0;

/** GET /api/friends/blocked — «Blocked» on the friends page, «Block» / «Unblock» in the conversation menu */
export async function loadBlocked(): Promise<void> {
  const seq = ++blockedSeq;
  try {
    const items = await chatApi.blocked();
    if (seq === blockedSeq) useChat.setState({ blocked: items });
  } catch {
    // the menu falls back to «Block»; the next friends:changed tries again
  }
}

export const isBlocked = (s: { blocked: BlockedPlayer[] | null }, pid: number): boolean =>
  s.blocked?.some((b) => b.publicId === pid) ?? false;

/** after block / unblock: writing is off at once; then the server truth (list, thread, blocked) is reloaded */
export function setBlocked(pid: number, blocked: boolean, who?: Omit<BlockedPlayer, 'publicId'>): void {
  useChat.setState((s) => {
    const rest = (s.blocked ?? []).filter((b) => b.publicId !== pid);
    return { blocked: blocked ? [...rest, { publicId: pid, nickname: who?.nickname ?? '', avatar: who?.avatar ?? '' }] : rest };
  });
  if (blocked) {
    patchThread(pid, (t) => (t.partner ? { partner: { ...t.partner, canWrite: false } } : {}));
    patchConversation(pid, () => ({ canWrite: false }));
  }
  void loadBlocked();
  if (useChat.getState().threads[pid]) void loadThread(pid);
  if (useChat.getState().conversations) void loadConversations();
}

/** after «Remove from friends»: writing is off at once (the conversation stays, read-only); then the server truth */
export function setUnfriended(pid: number): void {
  patchThread(pid, (t) => (t.partner ? { partner: { ...t.partner, canWrite: false } } : {}));
  patchConversation(pid, () => ({ canWrite: false }));
  if (useChat.getState().threads[pid]) void loadThread(pid);
  if (useChat.getState().conversations) void loadConversations();
}

export function resetChat(): void {
  listSeq++;
  blockedSeq++;
  readInFlight.clear();
  useChat.setState({ ...EMPTY });
}
